export const RALLY_STATUS_OPTIONS = ["upcoming", "active", "completed"];

export const initialRallyForm = {
  name: "",
  edition_number: "",
  // Rally can belong to multiple categories.
  categories: [],
  pricing: {},
  date: "",
  end_date: "",
  registration_start_date: "",
  registration_end_date: "",
  rally_start_date: "",
  location: "",
  organiser: "",
  description: "",
  status: "upcoming",
  is_featured: false,
  highlights: [""],
  stats_drivers: "0",
  stats_events: "0",
  stats_participants: "0",
};

const toDateInput = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

export const formatRallyDate = (dateValue) => {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};

export const getRallyCategories = (rally) => {
  if (Array.isArray(rally?.categories) && rally.categories.length) {
    return rally.categories.filter(Boolean);
  }

  const details = rally?.categories_details || rally?.category_details;
  if (Array.isArray(details) && details.length) {
    return details.filter(Boolean);
  }
  if (details && typeof details === "object") return [details];

  if (rally?.category) return [rally.category];
  return [];
};

export const getRallyCategoryLabel = (category) => {
  if (!category) return "";
  if (typeof category === "string") return category;
  return category.title || category.key || category._id || category.id || "";
};

export const getRallyCategoryLabels = (rally) =>
  getRallyCategories(rally).map(getRallyCategoryLabel).filter(Boolean);

export const getRallyCategoryValue = (rally) =>
  getRallyCategories(rally)
    .map((category) => {
      if (!category) return "";
      if (typeof category === "string") return category;
      return category._id || category.id || category.key || "";
    })
    .filter(Boolean);

export const mapRallyToForm = (rally) => ({
  name: rally?.name || "",
  edition_number:
    rally?.edition_number === 0 || rally?.edition_number
      ? String(rally.edition_number)
      : "",
  categories: getRallyCategoryValue(rally),
  pricing: getRallyPricingMap(rally),
  date: toDateInput(rally?.date),
  end_date: toDateInput(rally?.end_date),
  registration_start_date: toDateInput(rally?.registration_start_date),
  registration_end_date: toDateInput(rally?.registration_end_date),
  rally_start_date: toDateInput(rally?.rally_start_date),
  location: rally?.location || "",
  organiser: rally?.organiser || "",
  description: rally?.description || "",
  status: rally?.status || "upcoming",
  is_featured: Boolean(rally?.is_featured),
  highlights:
    Array.isArray(rally?.highlights) && rally.highlights.length
      ? rally.highlights
      : [""],
  stats_drivers: String(rally?.stats_drivers ?? 0),
  stats_events: String(rally?.stats_events ?? 0),
  stats_participants: String(rally?.stats_participants ?? 0),
});

export const validateRallyForm = (form) => {
  const errors = {};

  if (!form.name?.trim()) errors.name = "Name is required.";
  if (!form.date) errors.date = "Start date is required.";
  if (!form.location?.trim()) errors.location = "Location is required.";

  if (form.edition_number !== "" && Number(form.edition_number) < 0) {
    errors.edition_number = "Enter a valid edition number.";
  }

  ["stats_drivers", "stats_events", "stats_participants"].forEach((key) => {
    if (form[key] !== "" && Number(form[key]) < 0) {
      errors[key] = "Enter a valid number.";
    }
  });

  const pricingErrors = {};
  (form.categories || []).forEach((categoryId) => {
    const rawAmount = form.pricing?.[categoryId];
    if (rawAmount === "" || rawAmount == null) {
      pricingErrors[categoryId] = "Price is required.";
      return;
    }
    const amount = Number(rawAmount);
    if (Number.isNaN(amount) || amount < 0) {
      pricingErrors[categoryId] = "Enter a valid amount.";
    }
  });
  if (Object.keys(pricingErrors).length) {
    errors.pricing = pricingErrors;
  }

  return errors;
};

export const getRallyPricingMap = (rally) => {
  const map = {};

  const entries =
    rally?.pricing || rally?.prices || rally?.category_pricing || [];

  if (Array.isArray(entries)) {
    entries.forEach((item) => {
      const categoryId =
        item?.category_id ||
        item?.categoryId ||
        item?.category?._id ||
        item?.category?.id;
      const amount = item?.amount ?? item?.price;
      if (categoryId != null && amount != null && amount !== "") {
        map[categoryId] = String(amount);
      }
    });
  } else if (entries && typeof entries === "object") {
    Object.entries(entries).forEach(([key, value]) => {
      const amount =
        value && typeof value === "object"
          ? value.amount ?? value.price
          : value;
      if (amount != null && amount !== "") map[key] = String(amount);
    });
  }

  getRallyCategories(rally).forEach((category) => {
    if (!category || typeof category !== "object") return;
    const categoryId = category._id || category.id;
    const amount = category.amount ?? category.price ?? category.pricing;
    if (categoryId && amount != null && amount !== "" && map[categoryId] == null) {
      map[categoryId] = String(amount);
    }
  });

  return map;
};

export const getRallyEventId = (data) => {
  if (!data) return "";
  if (typeof data === "string") return data;
  const rally = normalizeRally(data);
  return rally?._id || rally?.id || "";
};

export const buildRallyPricingPayloads = (form) => {
  return (form.categories || [])
    .filter(Boolean)
    .map((categoryId) => ({
      category_id: categoryId,
      amount: Number(form.pricing?.[categoryId]),
    }))
    .filter((item) => Number.isFinite(item.amount) && item.amount >= 0);
};

export const buildRallyPayload = (form, options = {}) => {
  const { includeCategory = true } = options;

  const payload = {
    name: form.name.trim(),
    date: form.date,
    location: form.location.trim(),
    status: form.status || "upcoming",
    is_featured: Boolean(form.is_featured),
  };

  if (form.end_date) payload.end_date = form.end_date;
  if (form.registration_start_date) {
    payload.registration_start_date = form.registration_start_date;
  }
  if (form.registration_end_date) {
    payload.registration_end_date = form.registration_end_date;
  }
  if (form.rally_start_date) payload.rally_start_date = form.rally_start_date;
  if (form.organiser?.trim()) payload.organiser = form.organiser.trim();
  if (form.description?.trim()) payload.description = form.description.trim();

  if (includeCategory) {
    payload.categories = (Array.isArray(form.categories) ? form.categories : [])
      .filter(Boolean);
  }

  if (form.edition_number !== "") {
    payload.edition_number = Number(form.edition_number);
  }

  const highlights = (form.highlights || [])
    .map((item) => item.trim())
    .filter(Boolean);
  if (highlights.length) payload.highlights = highlights;

  payload.stats_drivers = Number(form.stats_drivers || 0);
  payload.stats_events = Number(form.stats_events || 0);
  payload.stats_participants = Number(form.stats_participants || 0);

  return payload;
};

export const formatRallyDateRange = (startDate, endDate) => {
  const start = formatRallyDate(startDate);
  const end = formatRallyDate(endDate);
  if (start === "-" && end === "-") return "-";
  if (end === "-") return start;
  if (start === "-") return end;
  return `${start} – ${end}`;
};

export const getRallyCategoryTitle = (rally) => {
  const labels = getRallyCategoryLabels(rally);
  return labels.length ? labels.join(", ") : "-";
};

export const formatRallyStatus = (status) => {
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const normalizeRallies = (data) => {
  if (Array.isArray(data)) return data;
  return data?.data || data?.rallies || data?.items || [];
};

export const normalizeRally = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data[0] || null;
  if (data._id || data.id || data.name) return data;
  return data.data || data.rally || data.event || null;
};

export const filterRallies = (rallies, searchTerm) => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return rallies;

  return rallies.filter((rally) => {
    const name = rally?.name?.toLowerCase() || "";
    const location = rally?.location?.toLowerCase() || "";
    const organiser = rally?.organiser?.toLowerCase() || "";
    const status = rally?.status?.toLowerCase() || "";
    const categories = getRallyCategoryTitle(rally).toLowerCase();
    const edition = String(rally?.edition_number ?? "");

    return (
      name.includes(term) ||
      location.includes(term) ||
      organiser.includes(term) ||
      status.includes(term) ||
      categories.includes(term) ||
      edition.includes(term)
    );
  });
};
